import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    AccelerationUnits,
    CurrentUserResponse,
    PressureUnits,
    TemperatureUnits,
    VelocityUnits,
} from '@kcftech/prometheus-client';
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { FIFTEEN_MINUTES_MS, THIRTY_SECONDS_MS } from 'configs/constants';
import { dataPlatformClient } from 'contexts/SDKClientContext';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { SelectedPoint } from 'pages/workbench/types';
import { queryClient } from 'utils/query';
import { Lookup } from 'utils/types';

const QUERY_COUNT_BEFORE_BACKOFF = 30; // After 30 failed attempts, start exponential backoff

const PressureUnitsToUnits: Lookup<PressureUnits, string> = {
    [PressureUnits.Mb]: 'mbar',
    [PressureUnits.Pascal]: 'Pa',
    [PressureUnits.Ppsi]: '[psi]',
};

/**
 * Gets the unit conversions for the user based on profile settings. Note that the values here are
 * currently manually mapped from the Prometheus Units API codes. There is currently no way to
 * map from Prometheus Units to Data Platform units via this route, but if that becomes available in the future,
 * this function should be updated to use that instead of hardcoding the mappings.
 */
export function getUnitsRequest(
    currentUser: CurrentUserResponse | undefined,
): Record<string, string> {
    const pressureUnits = currentUser?.Profile.PressureUnits
        ? PressureUnitsToUnits[currentUser.Profile.PressureUnits]
        : '';

    return {
        ['acceleration']:
            currentUser?.Profile.AccelerationUnits === AccelerationUnits.Mms2
                ? 'mm/s2'
                : '[g]',
        ['velocity']:
            currentUser?.Profile.VelocityUnits === VelocityUnits.Mmps
                ? 'mm/s'
                : '[in_i]/s',
        ['temperature']:
            currentUser?.Profile.TemperatureUnits === TemperatureUnits.Celsius
                ? 'Cel'
                : '[degF]',
        ['gauge-pressure']: pressureUnits,
        ['absolute-pressure']: pressureUnits,
        ['differential-pressure']: pressureUnits,
    };
}

export async function loadBurstData(
    selectedPoint: SelectedPoint,
    unitsRequest: Record<string, string>,
): Promise<DataPlatformApi.WaveformResponse> {
    return await dataPlatformClient.waveforms.getWaveform({
        timestamp: selectedPoint.xValue,
        legacyIndicatorId: selectedPoint.seriesId,
        units: unitsRequest,
    });
}

type BurstDataQueryConfig = {
    selectedPoint: SelectedPoint | undefined;
    unitsRequest: Record<string, string>;
};

export const burstDataQueries = {
    all: () => ['burst-data'] as const,
    waveforms: () => [...burstDataQueries.all(), 'waveform'] as const,
    waveform: ({ selectedPoint, unitsRequest }: BurstDataQueryConfig) =>
        queryOptions({
            queryKey: [
                ...burstDataQueries.waveforms(),
                selectedPoint?.seriesId,
                selectedPoint?.xValue,
                unitsRequest,
            ] as const,
            queryFn: selectedPoint
                ? () => loadBurstData(selectedPoint, unitsRequest)
                : skipToken,
        }),
};

type BurstDataQueryOptions = ReturnType<typeof burstDataQueries.waveform>;
type BurstDataQueryOverrides = Omit<BurstDataQueryOptions, 'queryKey' | 'queryFn'>;

export function useBurstData(
    selectedPoint: SelectedPoint | undefined,
    overrides?: BurstDataQueryOverrides,
) {
    const { data: currentUser } = useCurrentUserInfo();
    const unitsRequest = getUnitsRequest(currentUser);

    return useQuery({
        ...burstDataQueries.waveform({ selectedPoint, unitsRequest }),
        ...overrides,
        // The default query retry count is 3, which is not sufficient for burst data that may take a long time to process.
        // We set it to `true` to enable infinite retries, and implement our own retry delay logic with exponential backoff.
        retry: true,
        // If there was an error fetching the burst data, we want to refetch it when the window regains focus,
        // as the error may have been due to a temporary issue (e.g. burst processing latency)
        // that could be resolved by the time the user returns to the tab.
        refetchOnWindowFocus: (query) => query.state.error !== null,
        retryDelay: (failureCount) => {
            if (failureCount <= QUERY_COUNT_BEFORE_BACKOFF) {
                // For the first 15 minutes, retry every 30 seconds
                return THIRTY_SECONDS_MS;
            }

            // Exponential backoff starting at 30 seconds, maxing out at 15 minutes
            return Math.min(
                THIRTY_SECONDS_MS * 2 ** (failureCount - QUERY_COUNT_BEFORE_BACKOFF),
                FIFTEEN_MINUTES_MS,
            );
        },
    });
}

export function invalidateBurstData() {
    queryClient.invalidateQueries({
        queryKey: burstDataQueries.all(),
    });
}
